import { Link } from 'react-router-dom';
import { StarIcon } from 'lucide-react';
import { REVIEWS_CLAUSE } from '../../data/legal';
import { founder, salon } from '../../data/content';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { shopLabels } from '../../data/shopLabels';
import type { Product, Review } from '../../core/types/catalog';

/** Ervaringen: het gemiddelde links, geschreven reviews rechts. */
export function ProductReviews({
  product,
  reviews
}: {
  product: Product;
  reviews: Review[];
}) {
  return <section id="reviews" className="scroll-mt-28 border-t border-line bg-sand py-16 lg:py-20" aria-labelledby="reviews-title" data-id="element-8105" data-mp="2hsx2s5j48aao">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8106" data-mp="28rfs52oh90td">
        <div className="lg:col-span-4" data-id="element-8107" data-mp="12bdisluyrk1w">
          <h2 id="reviews-title" className={product.reviews > 0 ? 'sr-only' : 'font-display text-3xl font-light leading-tight text-ink'} data-id="element-8108" data-mp="12cewyz7a0mnu">
            Ervaringen
          </h2>
          {product.reviews > 0 && <>
              <p className="font-display text-6xl font-light text-ink" aria-hidden="true" data-id="element-8109" data-mp="167yxc6tl1ehh">
                {rating(product.rating)}
              </p>
              <p className="mt-2 text-[15px] text-muted" data-id="element-8110" data-mp="xa6h90g1e2t1">
                <span className="sr-only" data-id="element-8111" data-mp="xbpwqotvc9tr">{rating(product.rating)} van 5, </span>
                gemiddeld uit {plural(product.reviews, 'beoordeling', 'beoordelingen')}
              </p>
            </>}
        </div>
        <ul className="grid gap-8 sm:grid-cols-2 lg:col-span-8" data-id="element-8112" data-mp="sa0mvcidu279">
          {reviews.map(r => <li key={`${r.name}-${r.body.slice(0, 24)}`} className="border-t border-ink/15 pt-5" data-id="element-8113" data-mp="ej5gq7z9pz7d">
              {/* Altijd vijf sterren, de lege gedempt: zo lees je "4 van 5" in één oogopslag. */}
              <p className="flex gap-0.5" data-id="element-8114" data-mp="4kzl28421pay">
                <span className="sr-only" data-id="element-8115" data-mp="4l7xjyw9mslm">{r.rating} van 5</span>
                {Array.from({
              length: 5
            }).map((_, i) => <StarIcon key={i} className={`h-3.5 w-3.5 fill-current ${i < r.rating ? 'text-clay' : 'text-ink/15'}`} strokeWidth={0} aria-hidden="true" data-id="element-8116" data-mp="9lvokkkhv9fu" data-mpi={i} />)}
              </p>
              <blockquote className="mt-3 font-display text-lg leading-snug text-ink" data-id="element-8117" data-mp="26g5c52l7bfhv">“{r.body}”</blockquote>
              <p className="mt-3 text-[14px] text-muted" data-id="element-8118" data-mp="22eace2ix5a67">
                {r.name} · {r.context}
                {r.verified && ' · geverifieerd'}
              </p>
            </li>)}
          {reviews.length === 0 && <li className="text-[15px] leading-relaxed text-muted sm:col-span-2" data-id="element-8119" data-mp="2q5plqf944rqy">
              Nog geen geschreven review voor dit product. Gebruik je het al?{' '}
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4" data-id="element-8120" data-mp="2l8z43nhnto3f">
                Laat {founder.firstName} weten wat je ervan vindt
                <span className="sr-only" data-id="element-8121" data-mp="19pwf5oznmhvp">{shopLabels.viaWhatsApp}</span>
              </a>
              .
            </li>}
        </ul>
        {/* Omnibus: zeggen hoe reviews gecontroleerd worden, met de clausule erbij. */}
        {reviews.length > 0 && <p className="text-[13px] leading-relaxed text-muted lg:col-span-8 lg:col-start-5" data-id="element-8122" data-mp="zou40aib9m2i">
            Alleen wie het product kocht of de behandeling had, kan een review plaatsen. {founder.firstName} plaatst positieve én kritische reviews, ongewijzigd.{' '}
            <Link to={REVIEWS_CLAUSE} className="text-ink underline underline-offset-4" data-id="element-8123" data-mp="1dfpchlowe15k">
              Zo controleren we reviews
            </Link>
          </p>}
      </div>
    </section>;
}